// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/InviteUserModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Button, Field, Select, TextInput } from "../../../design-system/components";
import ModalShell from "../../compliance/modals/shared/ModalShell";
import ModalSection from "../../compliance/modals/shared/ModalSection";

export default function InviteUserModal({
  open,
  roles = [],
  saving,
  error,
  onClose,
  onInvite,
}) {
  const assignableRoles = useMemo(
    () => roles.filter((role) => role.is_assignable !== false),
    [roles]
  );
  const [email, setEmail] = useState("");
  const [invitedName, setInvitedName] = useState("");
  const [roleKey, setRoleKey] = useState("auditor");

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setInvitedName("");
    setRoleKey(assignableRoles[0]?.role_key || "auditor");
  }, [open, assignableRoles]);

  const submit = async (event) => {
    event.preventDefault();
    await onInvite?.({
      email: email.trim(),
      invited_name: invitedName.trim() || null,
      role_key: roleKey,
    });
  };

  return (
    <ModalShell
      className="enterprise-admin-modal-shell"
      overlayClassName="enterprise-admin-modal-overlay"
      headerClassName="enterprise-admin-modal-header"
      bodyClassName="enterprise-admin-modal-body"
      open={open}
      ariaLabel="Invite organization member"
      eyebrow="Member access"
      title="Invite a member"
      subtitle="Create a governed invitation for this organization."
      onClose={saving ? undefined : onClose}
      maxWidth="680px"
    >
      <form className="enterprise-admin-user-modal-form" onSubmit={submit}>
        <ModalSection
          title="Invitation details"
          description="The invited role, administrator, and invitation lifecycle are preserved in the administrative audit history."
          badge="Audited"
          badgeTone="info"
        >
          <div className="enterprise-admin-user-form-grid">
            <Field label="Email" required>
              {({ id, "aria-describedby": describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="person@company.com"
                  required
                  autoFocus
                />
              )}
            </Field>

            <Field label="Display name" optional>
              {({ id, "aria-describedby": describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  value={invitedName}
                  onChange={(event) => setInvitedName(event.target.value)}
                  placeholder="Member name"
                />
              )}
            </Field>

            <Field label="Initial role" className="enterprise-admin-user-form-wide" required>
              {({ id, "aria-describedby": describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={roleKey}
                  onChange={(event) => setRoleKey(event.target.value)}
                >
                  {assignableRoles.map((role) => (
                    <option key={role.role_key} value={role.role_key}>
                      {role.role_name || role.role_key}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          {error ? (
            <div className="enterprise-admin-inline-error enterprise-admin-modal-inline-error" role="alert">
              <strong>Invitation not created</strong>
              <span>
                {error === "invitation_already_pending"
                  ? "A pending invitation already exists for this email address."
                  : error}
              </span>
            </div>
          ) : null}
        </ModalSection>

        <div className="enterprise-admin-user-modal-actions">
          <Button type="submit" variant="primary" disabled={saving || !email.trim() || !roleKey}>
            {saving ? "Creating invitation…" : "Create invitation"}
          </Button>
          <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </ModalShell>
  );
}
